/**
 * Experience Settings Context - THE ONESCENT DIAL
 * Manages animation, sound, and haptic preferences across the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ANIMATION_INTENSITY } from '../utils/animationConfig';
import { 
  loadSoundPrefs, 
  saveSoundPrefs, 
  applySoundPrefs,
  getDefaultSoundPrefs,
  SoundEffects,
  AmbientSounds,
  resumeAudio
} from '../utils/soundSystem';
import {
  hapticLight,
  hapticDouble,
  hapticPulse,
  hapticSelection,
  isHapticEnabled,
  setHapticEnabled as setHapticEnabledGlobal,
  isVibrationSupported
} from '../utils/hapticFeedback';

// =============================================================================
// ANIMATION PREFERENCES
// =============================================================================

const ANIMATION_PREFS_KEY = 'tod_animation_preferences';
const ZOOM_PREFS_KEY = 'tod_zoom_level';

// Zoom constants
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2.0;
const ZOOM_STEP = 0.1;

const getDefaultAnimationPrefs = () => ({
  enabled: true,
  intensity: 'normal', // off, subtle, normal, enhanced
  reduceMotion: false, // Respect system preference
});

const loadAnimationPrefs = () => {
  try {
    const stored = localStorage.getItem(ANIMATION_PREFS_KEY);
    const prefs = stored ? { ...getDefaultAnimationPrefs(), ...JSON.parse(stored) } : getDefaultAnimationPrefs();
    
    // Check for system reduce motion preference
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      prefs.reduceMotion = true;
      if (prefs.intensity === 'enhanced') {
        prefs.intensity = 'subtle';
      }
    }
    
    // Auto-detect low-end devices and downgrade if user hasn't explicitly set preferences
    if (!stored) {
      const mem = navigator.deviceMemory; // GB (Chrome/Edge)
      const cores = navigator.hardwareConcurrency;
      if ((mem && mem < 4) || (cores && cores <= 2)) {
        prefs.intensity = 'subtle';
      }
    }
    
    return prefs;
  } catch {
    return getDefaultAnimationPrefs();
  }
};

const saveAnimationPrefs = (prefs) => {
  try {
    localStorage.setItem(ANIMATION_PREFS_KEY, JSON.stringify(prefs));
  } catch (e) {
    console.warn('Failed to save animation preferences:', e);
  }
};

// =============================================================================
// CONTEXT
// =============================================================================

const ExperienceContext = createContext(null);

export const ExperienceProvider = ({ children }) => {
  // Animation state
  const [animationPrefs, setAnimationPrefs] = useState(loadAnimationPrefs);
  
  // Sound state
  const [soundPrefs, setSoundPrefs] = useState(loadSoundPrefs);
  const [audioInitialized, setAudioInitialized] = useState(false);
  
  // Haptic state - call the function to get initial value
  const [hapticEnabled, setHapticEnabled] = useState(() => isHapticEnabled());
  
  // Zoom state
  const [zoomLevel, setZoomLevel] = useState(() => {
    try {
      const stored = localStorage.getItem(ZOOM_PREFS_KEY);
      return stored ? parseFloat(stored) : 1.0;
    } catch {
      return 1.0;
    }
  });
  
  // Settings panel visibility
  const [showSettings, setShowSettings] = useState(false);
  
  // Apply animation preferences
  useEffect(() => {
    saveAnimationPrefs(animationPrefs);
    
    // Add CSS class to body for global animation control
    document.body.classList.remove('animations-off', 'animations-subtle', 'animations-normal', 'animations-enhanced');
    if (!animationPrefs.enabled || animationPrefs.intensity === 'off') {
      document.body.classList.add('animations-off');
    } else {
      document.body.classList.add(`animations-${animationPrefs.intensity}`);
    }
  }, [animationPrefs]);
  
  // Global haptic feedback on interactive element clicks
  useEffect(() => {
    if (!hapticEnabled) return;
    
    const handleGlobalClick = (e) => {
      const target = e.target;
      
      // Check if clicked element or its parent is an interactive element
      const interactive = target.closest('button, [role="button"], a[href], input[type="checkbox"], input[type="radio"], [data-haptic]');
      
      if (interactive) {
        // Determine haptic type based on element
        const hapticType = interactive.dataset?.haptic || 'light';
        
        // Skip if element explicitly opts out
        if (interactive.dataset?.haptic === 'none') return;
        
        // Trigger appropriate haptic
        switch (hapticType) {
          case 'medium':
            hapticDouble();
            break;
          case 'selection':
            hapticSelection();
            break;
          case 'pulse':
            hapticPulse();
            break;
          default:
            hapticLight();
        }
      }
    };
    
    // Use capture phase to catch clicks before they're handled
    document.addEventListener('click', handleGlobalClick, { capture: true, passive: true });
    
    return () => {
      document.removeEventListener('click', handleGlobalClick, { capture: true });
    };
  }, [hapticEnabled]);
  
  // Apply sound preferences
  useEffect(() => {
    saveSoundPrefs(soundPrefs);
    if (audioInitialized) {
      applySoundPrefs(soundPrefs);
      // Auto-start ambient sound when enabled
      if (soundPrefs.enabled && soundPrefs.ambientEnabled) {
        const theme = soundPrefs.ambientTheme || 'spacio';
        AmbientSounds.start(theme);
      } else {
        AmbientSounds.stop();
      }
    }
  }, [soundPrefs, audioInitialized]);
  
  // Initialize audio on first user interaction
  const initializeAudio = useCallback(async () => {
    if (!audioInitialized) {
      await resumeAudio();
      setAudioInitialized(true);
      applySoundPrefs(soundPrefs);
      // Auto-start ambient if enabled
      if (soundPrefs.enabled && soundPrefs.ambientEnabled) {
        const theme = soundPrefs.ambientTheme || 'spacio';
        AmbientSounds.start(theme);
      }
    }
  }, [audioInitialized, soundPrefs]);
  
  // Haptic feedback helpers
  const triggerHaptic = useCallback((type = 'light') => {
    if (!hapticEnabled) return;
    switch (type) {
      case 'light':
        hapticLight();
        break;
      case 'double':
        hapticDouble();
        break;
      case 'pulse':
        hapticPulse();
        break;
      case 'selection':
        hapticSelection();
        break;
      default:
        hapticLight();
    }
  }, [hapticEnabled]);
  
  const toggleHaptic = useCallback(() => {
    const newEnabled = !hapticEnabled;
    setHapticEnabled(newEnabled);
    setHapticEnabledGlobal(newEnabled);
    // Give feedback when enabling
    if (newEnabled) {
      hapticDouble();
    }
  }, [hapticEnabled]);
  
  // Animation helpers
  const getAnimationConfig = useCallback((baseConfig) => {
    if (!animationPrefs.enabled || animationPrefs.intensity === 'off') {
      return {
        initial: {},
        animate: {},
        exit: {},
        transition: { duration: 0 }
      };
    }
    
    const intensityConfig = ANIMATION_INTENSITY[animationPrefs.intensity] || ANIMATION_INTENSITY.normal;
    
    return {
      ...baseConfig,
      transition: {
        ...baseConfig.transition,
        duration: (baseConfig.transition?.duration || 0.3) * intensityConfig.duration,
      }
    };
  }, [animationPrefs]);
  
  const shouldAnimate = animationPrefs.enabled && animationPrefs.intensity !== 'off';
  
  // Sound helpers
  const playSound = useCallback((soundName, options = {}) => {
    if (!soundPrefs.enabled || !soundPrefs.effectsEnabled) return;
    
    // Initialize audio if needed
    if (!audioInitialized) {
      initializeAudio().then(() => {
        if (SoundEffects[soundName]) {
          SoundEffects[soundName](options);
        }
      });
    } else {
      if (SoundEffects[soundName]) {
        SoundEffects[soundName](options);
      }
    }
  }, [soundPrefs, audioInitialized, initializeAudio]);
  
  const playThemedSound = useCallback((theme) => {
    if (!soundPrefs.enabled || !soundPrefs.effectsEnabled) return;
    
    if (!audioInitialized) {
      initializeAudio().then(() => SoundEffects.themed(theme));
    } else {
      SoundEffects.themed(theme);
    }
  }, [soundPrefs, audioInitialized, initializeAudio]);
  
  const startAmbient = useCallback((theme) => {
    if (!soundPrefs.enabled || !soundPrefs.ambientEnabled) return;
    
    if (!audioInitialized) {
      initializeAudio().then(() => AmbientSounds.start(theme));
    } else {
      AmbientSounds.start(theme);
    }
  }, [soundPrefs, audioInitialized, initializeAudio]);
  
  const stopAmbient = useCallback(() => {
    AmbientSounds.stop();
  }, []);
  
  // Update preferences
  const updateAnimationPrefs = useCallback((updates) => {
    setAnimationPrefs(prev => ({ ...prev, ...updates }));
  }, []);
  
  const updateSoundPrefs = useCallback((updates) => {
    setSoundPrefs(prev => ({ ...prev, ...updates }));
  }, []);
  
  // Quick toggles - with haptic feedback
  const toggleAnimations = useCallback(() => {
    triggerHaptic('double');
    setAnimationPrefs(prev => {
      const newEnabled = !prev.enabled;
      // Force a re-render by also updating intensity if needed
      return { 
        ...prev, 
        enabled: newEnabled,
        // Reset intensity to normal when re-enabling (if it was somehow 'off')
        intensity: newEnabled && prev.intensity === 'off' ? 'normal' : prev.intensity
      };
    });
  }, [triggerHaptic]);
  
  const toggleSound = useCallback(async () => {
    triggerHaptic('pulse');
    const newEnabled = !soundPrefs.enabled;
    
    if (newEnabled) {
      // Initialize audio first, then enable and start ambient
      await resumeAudio();
      setAudioInitialized(true);
      setSoundPrefs(prev => ({ ...prev, enabled: true }));
      // Start ambient sound immediately after enabling
      const theme = soundPrefs.ambientTheme || 'spacio';
      if (soundPrefs.ambientEnabled) {
        // Small delay to ensure state is updated
        setTimeout(() => {
          applySoundPrefs({ ...soundPrefs, enabled: true });
          AmbientSounds.start(theme);
        }, 50);
      }
    } else {
      // Disable and stop sounds
      AmbientSounds.stop();
      setSoundPrefs(prev => ({ ...prev, enabled: false }));
    }
  }, [soundPrefs, triggerHaptic]);
  
  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    triggerHaptic('double');
    setAnimationPrefs(getDefaultAnimationPrefs());
    setSoundPrefs(getDefaultSoundPrefs());
    setHapticEnabled(true);
    setHapticEnabledGlobal(true);
    setZoomLevel(1.0);
    localStorage.setItem(ZOOM_PREFS_KEY, '1.0');
    AmbientSounds.stop();
  }, [triggerHaptic]);
  
  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    triggerHaptic('light');
    setZoomLevel(prev => {
      const newZoom = Math.min(prev + ZOOM_STEP, MAX_ZOOM);
      localStorage.setItem(ZOOM_PREFS_KEY, newZoom.toString());
      return newZoom;
    });
  }, [triggerHaptic]);
  
  const handleZoomOut = useCallback(() => {
    triggerHaptic('light');
    setZoomLevel(prev => {
      const newZoom = Math.max(prev - ZOOM_STEP, MIN_ZOOM);
      localStorage.setItem(ZOOM_PREFS_KEY, newZoom.toString());
      return newZoom;
    });
  }, [triggerHaptic]);
  
  const resetZoom = useCallback(() => {
    triggerHaptic('double');
    setZoomLevel(1.0);
    localStorage.setItem(ZOOM_PREFS_KEY, '1.0');
  }, [triggerHaptic]);
  
  const setZoom = useCallback((level) => {
    const newZoom = Math.min(Math.max(level, MIN_ZOOM), MAX_ZOOM);
    setZoomLevel(newZoom);
    localStorage.setItem(ZOOM_PREFS_KEY, newZoom.toString());
  }, []);
  
  const value = {
    // Animation
    animationPrefs,
    updateAnimationPrefs,
    toggleAnimations,
    shouldAnimate,
    getAnimationConfig,
    
    // Sound
    soundPrefs,
    updateSoundPrefs,
    toggleSound,
    playSound,
    playThemedSound,
    startAmbient,
    stopAmbient,
    audioInitialized,
    initializeAudio,
    
    // Haptic
    hapticEnabled,
    toggleHaptic,
    triggerHaptic,
    hapticSupported: isVibrationSupported(),
    
    // Zoom
    zoomLevel,
    handleZoomIn,
    handleZoomOut,
    resetZoom,
    setZoom,
    MIN_ZOOM,
    MAX_ZOOM,
    canZoomIn: zoomLevel < MAX_ZOOM,
    canZoomOut: zoomLevel > MIN_ZOOM,
    
    // Settings UI
    showSettings,
    setShowSettings,
    resetToDefaults,
  };
  
  return (
    <ExperienceContext.Provider value={value}>
      {children}
    </ExperienceContext.Provider>
  );
};

export const useExperience = () => {
  const context = useContext(ExperienceContext);
  if (!context) {
    throw new Error('useExperience must be used within ExperienceProvider');
  }
  return context;
};

export default ExperienceContext;
